import { useState, useEffect, useCallback, useRef } from 'react';

export type GeoState = 'idle' | 'requesting' | 'active' | 'denied' | 'unsupported' | 'error';

export interface GeoPosition {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: number;
}

/**
 * Geolocation hook with high accuracy for AR positioning.
 *
 * State machine:
 * - idle        → not started
 * - requesting  → acquiring first fix
 * - active      → at least one fix obtained
 * - denied      → permission refused
 * - unsupported → geolocation API unavailable
 * - error       → timeout or position unavailable before any fix
 *
 * After the first fix, transient watch errors (timeout/unavailable) are
 * silently ignored so the view stays active.
 */
export function useGeolocation() {
  const [state, setState] = useState<GeoState>('idle');
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const hasFixRef = useRef(false);

  const start = useCallback(async () => {
    if (!('geolocation' in navigator)) {
      setState('unsupported');
      setError('Geolocation is not supported on this device.');
      return;
    }

    // Reset for a fresh start
    hasFixRef.current = false;
    setState('requesting');
    setError(null);

    const recordFix = (pos: GeolocationPosition) => {
      hasFixRef.current = true;
      setPosition({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        timestamp: pos.timestamp,
      });
      setState('active');
    };

    // One-shot first to surface permission denial early. If high-accuracy
    // times out, retry once with coarse accuracy so indoor/desktop users
    // still get a fix.
    navigator.geolocation.getCurrentPosition(
      recordFix,
      (err) => {
        // If the watch already got a fix, ignore one-shot errors
        if (hasFixRef.current) return;

        if (err.code === err.PERMISSION_DENIED) {
          setState('denied');
          setError('Location permission was denied. Enable location access to see nearby assets.');
        } else if (err.code === err.TIMEOUT) {
          // Coarse-accuracy retry for indoor / desktop
          navigator.geolocation.getCurrentPosition(
            recordFix,
            (retryErr) => {
              if (hasFixRef.current) return;
              setState('error');
              setError(
                retryErr.code === retryErr.PERMISSION_DENIED
                  ? 'Location permission was denied.'
                  : 'Could not determine your location. Check your GPS signal or try again.'
              );
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 10000 },
          );
        } else {
          setState('error');
          setError(err.message || 'Location is currently unavailable.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
    );

    // Start continuous watch for live updates
    watchIdRef.current = navigator.geolocation.watchPosition(
      recordFix,
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          hasFixRef.current = false;
          setState('denied');
          setError('Location permission was denied. Enable location access to see nearby assets.');
        } else if (!hasFixRef.current) {
          // Only surface watch errors if we never got a fix
          setState('error');
          setError(err.message || 'Could not determine your location.');
        }
        // If we already have a fix, ignore transient watch errors
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 3000 },
    );
  }, []);

  const stop = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    hasFixRef.current = false;
    setState('idle');
  }, []);

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  return { state, position, error, start, stop };
}
